import {
  Button,
  ButtonType,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useValidation,
  noop,
  sizing,
} from '@chillibean/component-library';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

export function RenameModal(props) {
  const { data = undefined, onClose = noop, onSave = noop, title = undefined, visible = false } = props;

  // VARIABLES
  const defaultValues = {
    plural: data?.plural,
    singular: data?.singular,
  };

  // HOOKS
  // Store
  const { loading } = useSelector((state) => state.protocolReducer.protocolAction);
  // Form
  const {
    formState: { errors },
    getValues,
    register,
    reset,
    setValue,
    trigger,
    watch,
  } = useForm({ defaultValues });
  // Custom
  const { validationMessages } = useValidation();

  const initializeForm = () => {
    register('singular', {
      maxLength: {
        message: validationMessages.maxLength(16),
        value: 16,
      },
      required: {
        message: validationMessages.required(),
        value: true,
      },
      value: defaultValues.singular,
    });
    register('plural', {
      maxLength: {
        message: validationMessages.maxLength(18),
        value: 18,
      },
      required: {
        message: validationMessages.required(),
        value: true,
      },
      value: defaultValues.plural,
    });
  };

  useEffect(() => {
    reset(defaultValues);
    initializeForm();
  }, [visible]);

  const onSaveHandler = async () => {
    const valid = await trigger();

    if (valid) {
      onSave(getValues());
    }
  };

  const handleUpdateSingularValue = (value) => {
    setValue('singular', value);
    setValue('plural', pluralize(value));
  };

  return (
    <Modal onClose={onClose} visible={visible}>
      <ModalHeader onClose={onClose} title={title} />
      <StyledModalBody>
        <Fields>
          <Input
            disabled={loading}
            error={errors.singular?.message}
            id="singular"
            label="Singular"
            name="Singular"
            onChange={(event) => handleUpdateSingularValue(event.target.value)}
            required
            value={watch('singular')}
          />
          <Input
            disabled={loading}
            error={errors.plural?.message}
            id="plural"
            label="Plural"
            name="Plural"
            onChange={(event) => setValue('plural', event.target.value)}
            required
            value={watch('plural')}
          />
        </Fields>
      </StyledModalBody>
      <ModalFooter>
        <Button disabled={loading} onClick={onClose} text="Cancel" type={ButtonType.TERTIARY} />
        <Button disabled={loading} loading={loading} onClick={onSaveHandler} text="Save" />
      </ModalFooter>
    </Modal>
  );
}

const StyledModalBody = styled(ModalBody)`
  display: grid;
  place-items: center;

  min-height: 150px;
`;

const Fields = styled.div`
  display: flex;
  gap: ${sizing.medium};

  width: 100%;
`;

RenameModal.propTypes = {
  data: PropTypes.shape({
    plural: PropTypes.string,
    singular: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  title: PropTypes.string,
  visible: PropTypes.bool,
};

export default RenameModal;
